import { capitalize } from 'lodash';

import * as OrderFn from '#/features/order/functions';
import { useTranslation } from '#/features/localization/utils';

import { Market } from '#/api/markets';
import { Order } from '#/api/orders';

import Title from '#/components/common/toast/Title';
import Info from '#/components/common/toast/Info';
import Buy from '#/components/common/toast/Buy';
import Sell from '#/components/common/toast/Sell';
import OrderSideIcon from '#/components/shared/OrderSideIcon';

interface Props {
  order: Order;
  market: Market;
}

export function SubmittedOrder({ order, market }: Props) {
  const { t } = useTranslation();
  const type = capitalize(t(order.type));
  const side = capitalize(t(order.side));
  const icon = <OrderSideIcon side={order.side} />;

  return (
    <Info icon={icon}>
      <Title>{t('{{type}} {{side}} Order Submitted', { type, side })}</Title>
      {order.size.toFormat()} {market.base_currency}{' '}
      {order.type === 'LIMIT' && `at $${order.price.toFormat()}`}
    </Info>
  );
}

export function CancelledMarketOrder({ order }: Props) {
  const { t } = useTranslation();
  const type = capitalize(t(order.type));
  const side = capitalize(t(order.side));
  const icon = <OrderSideIcon side={order.side} />;

  return (
    <Info icon={icon}>
      <Title>{t('{{type}} {{side}} Order Canceled', { type, side })}</Title>
      {order.cancel_reason != null &&
        `${t('Reason')}: ${formatReason(order.cancel_reason)}`}
    </Info>
  );
}

export function CancelledLimitOrder({ order, market }: Props) {
  const { t } = useTranslation();
  const type = capitalize(t(order.type));
  const side = capitalize(t(order.side));
  const icon = <OrderSideIcon side={order.side} />;

  return (
    <Info icon={icon}>
      <Title>{t('{{type}} {{side}} Order Canceled', { type, side })}</Title>
      {order.size.toFormat()} {market.base_currency} at $
      {order.price.toFormat()}
      <br />
      {order.cancel_reason != null &&
        `${t('Reason')}: ${formatReason(order.cancel_reason)}`}
    </Info>
  );
}

export function FilledMarketOrder({ order, market }: Props) {
  const { t } = useTranslation();
  const side = capitalize(t(order.side));
  const Container = order.side === 'BUY' ? Buy : Sell;

  return (
    <Container>
      <Title>{t('Market {{side}} Order Fill', { side })}</Title>
      {OrderFn.filledSize(order).toFormat()} {market.base_currency}
    </Container>
  );
}

// WIP handle translation for `cancel_reason` from https://docs.api.prod.paradex.trade/#sub-orders-market_name-operation
function formatReason(reason: string) {
  return capitalize(reason.replace(/_/gu, ' ').toLowerCase());
}
